// Central Database Switch, Routing & Master Backup/Reset Manager
// Controls where data is saved (Google Cloud Firestore, Onamae MySQL, or Local Storage)
// Prevents duplicate writes, eliminates slowness, and provides full Master Reset & Backup Import/Export.

import { UserProfile, PackageItem, ShipmentOrder, PersonalShopperOrder, YahooAuctionBid, SupportTicket, AppNotification, BlogPost, HomePopupConfig } from '../types';
import { MASTER_ADMIN_USER } from './firebase';

export type DatabaseTarget = 'firestore' | 'onamae' | 'local';

export type GlobalDatabasePreset = 
  | 'firestore_primary'   // Google Cloud Firestore Spark
  | 'onamae_primary'      // Servidor Onamae.com MySQL (sddb0040131222)
  | 'local_primary'       // Armazenamento Local / Offline
  | 'hybrid_failover'     // Firestore com failover automático Onamae
  | 'custom';             // Configuração granular por categoria

export interface DatabaseCategoryRouting {
  clients: DatabaseTarget;          // Lista de clientes
  suiteProducts: DatabaseTarget;    // Lista de produtos na suite (pacotes)
  shipments: DatabaseTarget;        // Lista de envios
  siteConfigs: DatabaseTarget;      // Configurações do site (branding, popup, taxas, etc.)
  salesFinancial: DatabaseTarget;   // Lista de registro de vendas / parte financeira
}

export interface DatabaseRoutingConfig {
  globalPreset: GlobalDatabasePreset;
  categories: DatabaseCategoryRouting;
  preventDuplicateWrites: boolean;
  clearCacheOnSwitch: boolean;
  lastUpdated: string;
}

const STORAGE_KEY = 'dojapao_database_routing_config_v1';

export const DEFAULT_DATABASE_ROUTING_CONFIG: DatabaseRoutingConfig = {
  globalPreset: 'firestore_primary',
  categories: {
    clients: 'firestore',
    suiteProducts: 'firestore',
    shipments: 'firestore',
    siteConfigs: 'firestore',
    salesFinancial: 'firestore',
  },
  preventDuplicateWrites: true,
  clearCacheOnSwitch: false,
  lastUpdated: new Date().toISOString(),
};

// Retrieve active configuration
export function getDatabaseRoutingConfig(): DatabaseRoutingConfig {
  if (typeof window === 'undefined') return DEFAULT_DATABASE_ROUTING_CONFIG;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        ...DEFAULT_DATABASE_ROUTING_CONFIG,
        ...parsed,
        categories: {
          ...DEFAULT_DATABASE_ROUTING_CONFIG.categories,
          ...(parsed.categories || {}),
        }
      };
    }
  } catch (e) {
    console.warn('[Database Switch] Read config notice:', e);
  }
  return DEFAULT_DATABASE_ROUTING_CONFIG;
}

// Save configuration and broadcast change
export function saveDatabaseRoutingConfig(config: Partial<DatabaseRoutingConfig>): DatabaseRoutingConfig {
  const current = getDatabaseRoutingConfig();
  const updated: DatabaseRoutingConfig = {
    ...current,
    ...config,
    categories: {
      ...current.categories,
      ...(config.categories || {}),
    },
    lastUpdated: new Date().toISOString(),
  };

  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('databaseRoutingConfigChanged', { detail: updated }));
    } catch (e) {
      console.warn('[Database Switch] Save config notice:', e);
    }
  }

  notifyListeners(updated);
  return updated;
}

// Helper to change global preset and update all categories accordingly
export function setGlobalDatabasePreset(preset: GlobalDatabasePreset): DatabaseRoutingConfig {
  let target: DatabaseTarget = 'firestore';
  if (preset === 'onamae_primary') target = 'onamae';
  else if (preset === 'local_primary') target = 'local';
  else if (preset === 'firestore_primary' || preset === 'hybrid_failover') target = 'firestore';

  if (preset === 'custom') {
    return saveDatabaseRoutingConfig({ globalPreset: 'custom' });
  }

  return saveDatabaseRoutingConfig({
    globalPreset: preset,
    categories: {
      clients: target,
      suiteProducts: target,
      shipments: target,
      siteConfigs: target,
      salesFinancial: target,
    }
  });
}

// Helper to set target for a single category
export function setCategoryDatabaseTarget(category: keyof DatabaseCategoryRouting, target: DatabaseTarget): DatabaseRoutingConfig {
  const current = getDatabaseRoutingConfig();
  const newCategories = {
    ...current.categories,
    [category]: target,
  };

  // Check if all are same or mixed
  const values = Object.values(newCategories);
  const allSame = values.every(v => v === values[0]);
  let newPreset: GlobalDatabasePreset = 'custom';
  if (allSame) {
    if (values[0] === 'firestore') newPreset = 'firestore_primary';
    else if (values[0] === 'onamae') newPreset = 'onamae_primary';
    else if (values[0] === 'local') newPreset = 'local_primary';
  }

  return saveDatabaseRoutingConfig({
    globalPreset: newPreset,
    categories: newCategories,
  });
}

// Check if a category should save to Firestore
export function shouldSaveToFirestore(category: keyof DatabaseCategoryRouting): boolean {
  const config = getDatabaseRoutingConfig();
  if (config.globalPreset === 'hybrid_failover') return true;
  return config.categories[category] === 'firestore';
}

// Check if a category should save to Onamae MySQL
export function shouldSaveToOnamae(category: keyof DatabaseCategoryRouting): boolean {
  const config = getDatabaseRoutingConfig();
  return config.categories[category] === 'onamae';
}

// Check if a category should save to Local Storage
export function shouldSaveToLocal(category: keyof DatabaseCategoryRouting): boolean {
  const config = getDatabaseRoutingConfig();
  return config.categories[category] === 'local';
}

// Listener subscriptions
const listeners = new Set<(config: DatabaseRoutingConfig) => void>();

export function subscribeDatabaseRouting(callback: (config: DatabaseRoutingConfig) => void): () => void {
  listeners.add(callback);
  callback(getDatabaseRoutingConfig());
  return () => {
    listeners.delete(callback);
  };
}

function notifyListeners(config: DatabaseRoutingConfig) {
  listeners.forEach((fn) => {
    try {
      fn(config);
    } catch (e) {
      console.warn('[Database Routing Listener Error]:', e);
    }
  });
}

// =========================================================================
// MASTER BACKUP & EXPORT / IMPORT ENGINE
// =========================================================================

export interface FullDatabaseBackupPayload {
  version: string;
  exportDate: string;
  app: string;
  origin: string;
  environment: string;
  stats: {
    clientsCount: number;
    suiteProductsCount: number;
    shipmentsCount: number;
    siteConfigsCount: number;
    salesFinancialCount: number;
    ticketsCount: number;
    notificationsCount: number;
    blogPostsCount: number;
  };
  data: {
    users: UserProfile[];
    packages: PackageItem[];
    shipments: ShipmentOrder[];
    personalShopperOrders?: PersonalShopperOrder[];
    yahooBids?: YahooAuctionBid[];
    siteConfigs: {
      homePopup?: HomePopupConfig | null;
      branding?: any;
      currencyRates?: any;
      paymentGateways?: any;
      smtpConfig?: any;
    };
    tickets?: SupportTicket[];
    notifications?: AppNotification[];
    blogPosts?: BlogPost[];
  };
}

export function generateDatabaseBackupJson(payload: {
  users: UserProfile[];
  packages: PackageItem[];
  shipments: ShipmentOrder[];
  personalShopperOrders?: PersonalShopperOrder[];
  yahooBids?: YahooAuctionBid[];
  tickets?: SupportTicket[];
  notifications?: AppNotification[];
  blogPosts?: BlogPost[];
  homePopupConfig?: HomePopupConfig;
}): FullDatabaseBackupPayload {
  const homePopup = payload.homePopupConfig || (() => {
    try {
      const saved = localStorage.getItem('home_popup_config');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  })();

  const currencyRates = (() => {
    try {
      const saved = localStorage.getItem('dojapao_currency_rates');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  })();

  const branding = (() => {
    try {
      const saved = localStorage.getItem('dojapao_branding');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  })();

  const paymentGateways = (() => {
    try {
      const saved = localStorage.getItem('dojapao_payment_gateways');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  })();

  const users = payload.users || [];
  const packages = payload.packages || [];
  const shipments = payload.shipments || [];
  const personalShopperOrders = payload.personalShopperOrders || [];
  const yahooBids = payload.yahooBids || [];
  const tickets = payload.tickets || [];
  const notifications = payload.notifications || [];
  const blogPosts = payload.blogPosts || [];

  const salesFinancialCount = personalShopperOrders.length + yahooBids.length + shipments.filter(s => Boolean(s.paymentDetails)).length;

  return {
    version: '2.0.0',
    exportDate: new Date().toISOString(),
    app: 'DoJapaoPraSuaCasa',
    origin: typeof window !== 'undefined' ? window.location.origin : 'https://dojapaoprasuacasa.com',
    environment: 'Production Cloud / Onamae',
    stats: {
      clientsCount: users.length,
      suiteProductsCount: packages.length,
      shipmentsCount: shipments.length,
      siteConfigsCount: 4,
      salesFinancialCount,
      ticketsCount: tickets.length,
      notificationsCount: notifications.length,
      blogPostsCount: blogPosts.length,
    },
    data: {
      users,
      packages,
      shipments,
      personalShopperOrders,
      yahooBids,
      siteConfigs: {
        homePopup,
        branding,
        currencyRates,
        paymentGateways,
      },
      tickets,
      notifications,
      blogPosts,
    }
  };
}

export function downloadBackupFile(backup: FullDatabaseBackupPayload, customFileName?: string) {
  const dateStr = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const fileName = customFileName || `backup_banco_dados_dojapao_${dateStr}.json`;
  const jsonStr = JSON.stringify(backup, null, 2);

  if (typeof window !== 'undefined') {
    const blob = new Blob([jsonStr], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

export interface BackupValidationResult {
  valid: boolean;
  error?: string;
  backup?: FullDatabaseBackupPayload;
  summary?: {
    clients: number;
    suiteProducts: number;
    shipments: number;
    salesFinancial: number;
    siteConfigs: number;
    exportDate: string;
    version: string;
  };
}

export function validateAndParseBackupFile(fileContent: string): BackupValidationResult {
  try {
    const parsed = JSON.parse(fileContent);
    if (!parsed || typeof parsed !== 'object') {
      return { valid: false, error: 'O arquivo selecionado não contém um JSON válido.' };
    }

    // Determine if it's structured full backup or raw array
    let users: UserProfile[] = [];
    let packages: PackageItem[] = [];
    let shipments: ShipmentOrder[] = [];
    let personalShopperOrders: PersonalShopperOrder[] = [];
    let yahooBids: YahooAuctionBid[] = [];
    let siteConfigs: any = {};

    if (parsed.data && typeof parsed.data === 'object') {
      users = Array.isArray(parsed.data.users) ? parsed.data.users : [];
      packages = Array.isArray(parsed.data.packages) ? parsed.data.packages : [];
      shipments = Array.isArray(parsed.data.shipments) ? parsed.data.shipments : [];
      personalShopperOrders = Array.isArray(parsed.data.personalShopperOrders) ? parsed.data.personalShopperOrders : [];
      yahooBids = Array.isArray(parsed.data.yahooBids) ? parsed.data.yahooBids : [];
      siteConfigs = parsed.data.siteConfigs || {};
    } else if (Array.isArray(parsed)) {
      // Could be an array of users or packages
      if (parsed.length > 0 && parsed[0].suiteNumber && parsed[0].email) {
        users = parsed;
      } else if (parsed.length > 0 && parsed[0].trackingIn) {
        packages = parsed;
      }
    } else {
      // Root keys
      users = Array.isArray(parsed.users) ? parsed.users : [];
      packages = Array.isArray(parsed.packages) ? parsed.packages : [];
      shipments = Array.isArray(parsed.shipments) ? parsed.shipments : [];
    }

    const payload: FullDatabaseBackupPayload = {
      version: parsed.version || '1.0.0',
      exportDate: parsed.exportDate || new Date().toISOString(),
      app: parsed.app || 'DoJapaoPraSuaCasa',
      origin: parsed.origin || 'Imported PC File',
      environment: 'Restored Backup',
      stats: {
        clientsCount: users.length,
        suiteProductsCount: packages.length,
        shipmentsCount: shipments.length,
        siteConfigsCount: Object.keys(siteConfigs).length,
        salesFinancialCount: personalShopperOrders.length + yahooBids.length,
        ticketsCount: Array.isArray(parsed.data?.tickets) ? parsed.data.tickets.length : 0,
        notificationsCount: Array.isArray(parsed.data?.notifications) ? parsed.data.notifications.length : 0,
        blogPostsCount: Array.isArray(parsed.data?.blogPosts) ? parsed.data.blogPosts.length : 0,
      },
      data: {
        users,
        packages,
        shipments,
        personalShopperOrders,
        yahooBids,
        siteConfigs,
        tickets: parsed.data?.tickets || [],
        notifications: parsed.data?.notifications || [],
        blogPosts: parsed.data?.blogPosts || [],
      }
    };

    return {
      valid: true,
      backup: payload,
      summary: {
        clients: users.length,
        suiteProducts: packages.length,
        shipments: shipments.length,
        salesFinancial: personalShopperOrders.length + yahooBids.length + shipments.filter(s => Boolean(s.paymentDetails)).length,
        siteConfigs: Object.keys(siteConfigs).length,
        exportDate: payload.exportDate,
        version: payload.version,
      }
    };
  } catch (err: any) {
    return {
      valid: false,
      error: `Falha ao processar arquivo: ${err.message || 'Formato inválido'}`,
    };
  }
}
